'use client'

import * as React from 'react'
import { ChevronDown } from 'lucide-react'

import {
  Card,
  CardContent,
  CardDescription,
  CardTitle,
} from '@/components/ui/card'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'

type SectionCardCommonProps = {
  /** Whether the content is open by default. Defaults to true. */
  defaultOpen?: boolean
  /** Class applied to the outer Card. */
  className?: string
  /** Class applied to the auto CardContent wrapper. Ignored when `bareContent` is true. */
  contentClassName?: string
  /**
   * When true, render children directly inside CollapsibleContent without
   * wrapping in CardContent. Use when children include their own
   * <CardContent>/<CardFooter>.
   */
  bareContent?: boolean
  /**
   * Whether to render a `theme-card-divider` border under the header when the
   * card is open. Defaults to true. Set to false when content provides its
   * own visual separator (e.g. a hardcoded `border-t` on the inner content).
   */
  headerDivider?: boolean
  children: React.ReactNode
}

type SectionCardTitleProps = SectionCardCommonProps & {
  /** Title rendered with CardTitle (picks up theme-card-title styles). */
  title: React.ReactNode
  /** Optional description rendered with CardDescription. */
  description?: React.ReactNode
  /** Optional element rendered to the left of the title (e.g. an icon). */
  icon?: React.ReactNode
  trigger?: never
}

type SectionCardSlotProps = SectionCardCommonProps & {
  /**
   * Full custom header content. The chevron is appended automatically.
   * Use only when the title/description/icon API isn't sufficient — prefer the
   * structured props so cards pick up theme-card-title/theme-card-content.
   */
  trigger: React.ReactNode
  title?: never
  description?: never
  icon?: never
}

export type SectionCardProps = SectionCardTitleProps | SectionCardSlotProps

export function SectionCard(props: SectionCardProps) {
  const {
    defaultOpen = true,
    className,
    contentClassName,
    bareContent = false,
    headerDivider = true,
    children,
  } = props

  const customTrigger = 'trigger' in props ? props.trigger : null
  const title = 'title' in props ? props.title : null
  const description = 'description' in props ? props.description : null
  const icon = 'icon' in props ? props.icon : null

  return (
    <Collapsible defaultOpen={defaultOpen} className="w-full">
      <Card className={cn('w-full text-left', className)}>
        <CollapsibleTrigger asChild>
          <div
            className={cn(
              'group flex w-full cursor-pointer flex-row items-center gap-3 p-4 transition-colors hover:bg-muted/50 md:p-6',
              headerDivider && 'theme-card-divider data-[state=open]:border-b',
            )}
          >
            {customTrigger ? (
              customTrigger
            ) : (
              <>
                {icon ? <span className="shrink-0">{icon}</span> : null}
                <div className="min-w-0 flex-1">
                  <CardTitle>{title}</CardTitle>
                  {description ? (
                    <CardDescription className="mt-1">{description}</CardDescription>
                  ) : null}
                </div>
              </>
            )}
            <ChevronDown
              aria-hidden="true"
              className="ml-auto h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-200 group-data-[state=closed]:-rotate-90"
            />
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          {bareContent ? (
            children
          ) : (
            <CardContent className={cn('space-y-6 p-4 md:p-6', contentClassName)}>
              {children}
            </CardContent>
          )}
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}
